import z from 'zod';
import { TFunction } from 'i18next';
import { Button, Checkbox, notification } from 'antd';
import { Control, Controller, useForm, UseFormTrigger } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { AppModal } from '@components/form/Modal';
import { AppInput } from '@components/form/input/app-input';
import { AppSelect } from '@components/form/select/app-select';
import { AppPhoneInput } from '@components/form/phone-input/app-phone-input';
import { FormFieldError, Label } from '@components/form/text-area/app-text-area';
import { AppAlert } from '@components/antd';
import { RiArrowRightSLine, RiArrowRightUpLine, RiErrorWarningFill } from 'react-icons/ri';
import { InfoCircleOutlined } from '@ant-design/icons';
import { capitalize } from '@shared/utils/capitalize';
import { phoneParser } from '@shared/utils';
import { useModal } from '@shared/hooks/use-modal';
import clsx from 'clsx';
import { RegulatoryComplianceUseCaseEnum, useRequestFreeRegulatoryComplianceMutation } from '@/types/generated/graphql';
import { usePersonalInformation } from '@modules/settings/my-numbers/purchase-number/components/use-personal-information';
import { useUploadMultiMedia } from '@modules/settings/my-numbers/purchase-number/hooks/use-upload-media';
import { BundleVerificationFields } from '@modules/settings/my-numbers/purchase-number/components/bundle-verification-fields';
import { AppSelectCountry } from '@modules/settings/my-numbers/purchase-number/components/app-select-country';
import { numberUseCaseSchema } from '@modules/settings/my-numbers/purchase-number/schema/request-number.schema';
import { requestNumberDefaultValues } from '@modules/settings/my-numbers/purchase-number/constants/purchase-number.constant';
import { buildClaimCartNumbers, ClaimNumberSelection } from '../utils/build-claim-cart-numbers';

const numberUseCaseOptions = [
  { value: 'individual', label: 'Personal' },
  { value: 'business', label: 'Business' },
];

// GB-only free-claim flow: bundle verification only opens for a GB pair (see use-claim-numbers-form.ts),
// so unlike the paid flow this never needs the Australian personal-link field.
const bundleVerificationFormSchema = (t: TFunction) =>
  z.object({
    useCase: numberUseCaseSchema(t, false, true),
    agree: z.literal(true, { errorMap: () => ({ message: t('you_must_agree_to_continue') }) }),
  });

type BundleVerificationFormSchema = z.infer<ReturnType<typeof bundleVerificationFormSchema>>;

export type BundleVerificationModalProps = {
  open: boolean;
  onClose: VoidFunction;
  selections: ClaimNumberSelection[];
  capabilities: string[];
  fieldRequirements: { isIdentityFieldRequired: boolean; isAddressFieldRequired: boolean } | null;
  onRequested: VoidFunction;
};

export function BundleVerificationModal({
  open,
  onClose,
  selections,
  capabilities,
  fieldRequirements,
  onRequested,
}: BundleVerificationModalProps) {
  const { t } = useTranslation();
  const personalInfo = usePersonalInformation();

  const personalInfoModal = useModal();
  const businessInfoModal = useModal();

  const identityFrontUploadMedia = useUploadMultiMedia({ limit: 1 });
  const identityBackUploadMedia = useUploadMultiMedia({ limit: 1 });
  const addressProofUploadMedia = useUploadMultiMedia({ limit: 1 });

  const { control, handleSubmit, watch, trigger, formState } = useForm<BundleVerificationFormSchema>({
    mode: 'all',
    defaultValues: { ...requestNumberDefaultValues, ...personalInfo, agree: false as unknown as true },
    resolver: zodResolver(bundleVerificationFormSchema(t)),
  });
  const agree = watch('agree');
  const useCase = watch('useCase');

  const errors = formState.errors;
  const issueInUseCase = !!errors?.useCase?.address;
  const issueInPersonalUseCase =
    errors?.useCase?.firstName || errors?.useCase?.lastName || errors?.useCase?.email || errors?.useCase?.phoneNumber;
  const issueInBusinessUseCase =
    errors?.useCase?.businessName ||
    errors?.useCase?.businessPhoneNumber ||
    errors?.useCase?.registrationNumber ||
    errors?.useCase?.websiteUrl;
  const hasUseCaseSelectionIssue = useCase?.useCase === 'individual' ? issueInPersonalUseCase : issueInBusinessUseCase;

  const [requestFreeRegulatoryCompliance, { loading }] = useRequestFreeRegulatoryComplianceMutation({
    onCompleted: (data) => {
      notification.success({
        message: t('request_regulatory_compliance_success'),
        description: data?.requestFreeRegulatoryCompliance?.message,
      });
      onRequested();
      onClose();
    },
    onError: (error) => notification.error({ message: error.message }),
    update: (cache) => {
      // Requesting bundle verification creates new Pending rows and reserves a reward slot
      // immediately (per the API contract), so the Pending/Active/Failed tabs and their
      // header counts can all go stale otherwise.
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'inProcessPhoneNumbersV2' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'channelsV2' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'purchaseFailedPhoneNumbers' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'phoneNumberStatusCount' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'checkConfigurationAndRewardOfChannel' });
      cache.gc();
    },
  });

  const firstNumber = phoneParser.format(selections[0]?.phoneNumber);
  const othersCount = selections.length - 1;

  const handleRequestNumber = async () => {
    const success = await trigger(['useCase', 'agree']);
    if (!success) return;
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: BundleVerificationFormSchema) => {
    const identityDocumentId = identityFrontUploadMedia?.tempFileList?.[0]?.preview?.data?.id;
    const identityDocumentBackId = identityBackUploadMedia?.tempFileList?.[0]?.preview?.data?.id;
    const addressDocumentId = addressProofUploadMedia?.tempFileList?.[0]?.preview?.data?.id;

    const businessInformation = {
      businessName: data?.useCase?.businessName,
      businessPhoneNumber: data?.useCase?.businessPhoneNumber,
      registrationNumber: data?.useCase?.registrationNumber,
      websiteUrl: data?.useCase?.websiteUrl,
    };

    await requestFreeRegulatoryCompliance({
      variables: {
        input: {
          ...(data?.useCase?.useCase === 'business' && businessInformation),
          useCase: data.useCase?.useCase as RegulatoryComplianceUseCaseEnum,
          firstName: data.useCase?.firstName,
          lastName: data.useCase?.lastName,
          email: data.useCase?.email,
          phoneNumber: data.useCase?.phoneNumber,
          address: {
            country: data?.useCase?.address?.country,
            suburb: data?.useCase?.address?.suburb,
            street: data?.useCase?.address?.suburb,
            state: data?.useCase?.address?.state,
            zipcode: data?.useCase?.address?.zip,
          },
          identityDocumentId,
          identityDocumentBackId,
          addressDocumentId,
          channelsRequested: buildClaimCartNumbers(selections, capabilities),
        },
      },
    });
  };

  return (
    <AppModal
      open={open}
      onCancel={onClose}
      classNames={{ content: 'w-full max-w-[680px]!', footer: 'hidden' }}
      width={680}
      style={{ marginTop: 20, marginBottom: 20 }}
      title={
        <AppModal.Header>
          <AppModal.Title>
            {othersCount > 0
              ? t('request_number_title_other', { number: firstNumber, count: othersCount })
              : t('request_number_title_one', { number: firstNumber })}
          </AppModal.Title>
          <AppModal.SubTitle>{t('please_add_friendly_name_to_the_number_you_are_about_to_request')}</AppModal.SubTitle>
        </AppModal.Header>
      }
    >
      <AppModal.Body>
        <div className="space-y-4">
          <div className="space-y-4 border border-gray-700 rounded-2xl p-4 mt-2">
            <div>
              <Label>{capitalize(t('friendly_names'))}</Label>
              <div className="flex flex-wrap gap-2 mt-1.5 border border-gray-500 rounded-md p-1">
                {selections.slice(0, 4).map((selection) => (
                  <span key={selection.phoneNumber} className="bg-gray-300 text-text-700 text-sm rounded-md px-3 py-1">
                    {selection.friendlyName}
                  </span>
                ))}
                {selections.length > 4 && (
                  <span className="bg-gray-300 text-text-700 text-sm rounded-md px-3 py-1">
                    +{selections.length - 4}...
                  </span>
                )}
              </div>
            </div>

            <AppSelect
              control={control}
              name="useCase.useCase"
              label={t('number_use_case')}
              size="middle"
              options={numberUseCaseOptions}
            />

            <div
              className={clsx(
                'bg-gray-100 border border-gray-400 text-text-500 rounded-2xl px-5 py-4 flex justify-between items-center',
                (issueInUseCase || hasUseCaseSelectionIssue) && 'border-red-500'
              )}
            >
              <div>
                <p className="text-text-700">
                  {useCase?.useCase === 'individual'
                    ? capitalize(t('personal_information'))
                    : capitalize(t('business_information'))}
                </p>
                <p className={clsx('text-sm', (issueInUseCase || hasUseCaseSelectionIssue) && 'text-red-500')}>
                  {t('add_your_information_to_complete_your_profile')}
                </p>
              </div>

              <Button
                className="uppercase tracking-wider text-sm!"
                icon={<RiArrowRightSLine />}
                iconPosition="end"
                type="text"
                onClick={() => {
                  if (useCase?.useCase === 'individual') return personalInfoModal.handleOpen();
                  return businessInfoModal.handleOpen();
                }}
              >
                {t('edit')}
              </Button>
            </div>
          </div>

          <AppAlert
            showIcon
            icon={<RiErrorWarningFill />}
            type="info"
            className="app-alert rounded-lg! mb-4!"
            message={
              <div className="text-sm">
                {t('following_are_documents_required_by_regulatory_body_in_order_to_procure_this_number') + ' '}
                <a
                  href="https://www.calilio.com/guidelines"
                  className="inline-flex items-center gap-1 border-b border-primary-500 leading-tight"
                  target="_blank"
                >
                  {t('learn_more_about_these_requirements') + ' '} <RiArrowRightUpLine fontSize={14} />
                </a>
              </div>
            }
          />

          <BundleVerificationFields
            isIdentityFieldRequired={fieldRequirements?.isIdentityFieldRequired}
            isAddressFieldRequired={fieldRequirements?.isAddressFieldRequired}
            identityFrontUploadMedia={identityFrontUploadMedia}
            identityBackUploadMedia={identityBackUploadMedia}
            addressProofUploadMedia={addressProofUploadMedia}
          />

          {(fieldRequirements?.isIdentityFieldRequired || fieldRequirements?.isAddressFieldRequired) && (
            <div>
              <p className="text-sm text-amber-600">
                {t('please_upload_document_above_alternatively_you_can_upload_the_required_documents')}
              </p>
            </div>
          )}

          <Controller
            name="agree"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div>
                <div className="flex gap-2 items-start">
                  <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} id="agree" />
                  <label className="text-sm leading-tight" htmlFor="agree">
                    {t('agree_to_proceed_with_alternative_number_approval')}
                  </label>
                </div>
                <FormFieldError message={error?.message} />
              </div>
            )}
          />
        </div>

        <AppModal.Footer className="mt-4">
          <AppModal.Button className="w-full capitalize!" onClick={onClose}>
            {capitalize(t('back'))}
          </AppModal.Button>
          <AppModal.Button
            color="primary"
            loading={loading}
            disabled={!agree}
            className="w-full capitalize!"
            onClick={handleRequestNumber}
          >
            {t('request_number')}
          </AppModal.Button>
        </AppModal.Footer>
      </AppModal.Body>

      <BusinessInfoModal control={control} trigger={trigger} businessInfoModal={businessInfoModal} />
      <PersonalInfoModal control={control} trigger={trigger} personalInfoModal={personalInfoModal} />
    </AppModal>
  );
}

type SubModalProps = {
  control: Control<BundleVerificationFormSchema>;
  trigger: UseFormTrigger<BundleVerificationFormSchema>;
  businessInfoModal?: ReturnType<typeof useModal>;
  personalInfoModal?: ReturnType<typeof useModal>;
};

function BusinessInfoModal({ control, trigger, businessInfoModal }: SubModalProps) {
  const { t } = useTranslation();

  return (
    <AppModal
      centered
      open={businessInfoModal?.open}
      classNames={{ content: '!max-w-[420px]' }}
      onCancel={businessInfoModal?.handleClose}
      title={
        <AppModal.Header>
          <AppModal.Title>
            <h4 className="font-medium text-[20px] text-text-900 capitalize">{t('business_information')}</h4>
          </AppModal.Title>
        </AppModal.Header>
      }
      footer={
        <AppModal.Footer>
          <AppModal.Button onClick={businessInfoModal?.handleClose} className="w-full capitalize!">
            {t('back')}
          </AppModal.Button>
          <AppModal.Button
            color="primary"
            className="w-full capitalize!"
            onClick={async () => {
              const success = await trigger([
                'useCase.businessName',
                'useCase.businessPhoneNumber',
                'useCase.registrationNumber',
                'useCase.websiteUrl',
                'useCase.address.country',
                'useCase.address.state',
                'useCase.address.zip',
                'useCase.address.suburb',
              ]);

              if (success) {
                businessInfoModal?.handleClose();
                notification.success({ message: t('your_business_information_is_saved') });
              }
            }}
          >
            {t('save_one')}
          </AppModal.Button>
        </AppModal.Footer>
      }
    >
      <AppModal.Body>
        <div className="space-y-4 mt-6">
          <AppInput
            control={control}
            name="useCase.businessName"
            label={capitalize(t('business_name'))}
            placeholder={t('business_name')}
            required
          />

          <AppInput
            control={control}
            name="useCase.registrationNumber"
            label={capitalize(t('business_registration_number'))}
            placeholder={t('business_registration_number')}
            required
          />

          <AppInput
            control={control}
            name="useCase.websiteUrl"
            label={capitalize(t('business_website'))}
            placeholder="https://"
            required
          />

          <div>
            <AppPhoneInput
              control={control}
              label={capitalize(t('phone_number'))}
              name="useCase.businessPhoneNumber"
              placeholder={t('enter_your_phone_number')}
              required
            />
            <p className="text-sm text-text-400 mt-2">
              <InfoCircleOutlined className="size-3 mr-2" />
              {t('valid_phone_number_not_acquired_from_CPaaS_virtual_number_provides')}
            </p>
          </div>

          <AppInput
            control={control}
            name="useCase.address.suburb"
            label={capitalize(t('business_address'))}
            placeholder={t('operational_address_of_the_company')}
            required
          />
          <AppSelectCountry
            name="useCase.address.country"
            control={control}
            label={capitalize(t('country', { count: 1 }))}
            required
          />

          <div className="flex gap-4">
            <AppInput
              control={control}
              name="useCase.address.state"
              label={t('state_slash_province')}
              placeholder={capitalize(t('province'))}
              required
            />
            <AppInput
              control={control}
              name="useCase.address.zip"
              label={capitalize(t('postal_code'))}
              placeholder={capitalize(t('postal_code'))}
              required
            />
          </div>
        </div>
      </AppModal.Body>
    </AppModal>
  );
}

function PersonalInfoModal({ control, trigger, personalInfoModal }: SubModalProps) {
  const { t } = useTranslation();

  return (
    <AppModal
      centered
      open={personalInfoModal?.open}
      classNames={{ content: '!max-w-[420px]' }}
      onCancel={personalInfoModal?.handleClose}
      title={
        <AppModal.Header>
          <AppModal.Title>
            <h4 className="font-medium text-[20px] text-text-900 capitalize!">{t('personal_information')}</h4>
          </AppModal.Title>
        </AppModal.Header>
      }
      footer={
        <AppModal.Footer>
          <AppModal.Button onClick={personalInfoModal?.handleClose} className="w-full capitalize!">
            {t('back')}
          </AppModal.Button>
          <AppModal.Button
            color="primary"
            onClick={async () => {
              const success = await trigger([
                'useCase.firstName',
                'useCase.lastName',
                'useCase.email',
                'useCase.phoneNumber',
                'useCase.address.country',
                'useCase.address.state',
                'useCase.address.zip',
                'useCase.address.suburb',
              ]);

              if (success) {
                personalInfoModal?.handleClose();
                notification.success({ message: t('your_personal_information_is_saved') });
              }
            }}
            className="w-full capitalize!"
          >
            {t('save_one')}
          </AppModal.Button>
        </AppModal.Footer>
      }
    >
      <AppModal.Body>
        <div className="space-y-4 mt-6">
          <div className="flex gap-4">
            <AppInput
              control={control}
              name="useCase.firstName"
              label={capitalize(t('first_name'))}
              placeholder={capitalize(t('first_name'))}
              required
            />

            <AppInput
              control={control}
              name="useCase.lastName"
              label={capitalize(t('last_name'))}
              placeholder={capitalize(t('last_name'))}
              required
            />
          </div>

          <AppInput
            control={control}
            name="useCase.email"
            label={capitalize(t('contact_email'))}
            placeholder={capitalize(t('contact_email'))}
            required
          />

          <div>
            <AppPhoneInput
              control={control}
              label={capitalize(t('phone_number'))}
              name="useCase.phoneNumber"
              placeholder={capitalize(t('phone_number'))}
              required
            />
            <p className="text-sm text-text-400 mt-2">
              <InfoCircleOutlined className="size-3 mr-2" />
              {t('valid_phone_number_not_acquired_from_CPaaS_virtual_number_provides')}
            </p>
          </div>

          <AppInput
            control={control}
            name="useCase.address.suburb"
            label={capitalize(t('address_line_1'))}
            placeholder={capitalize(t('address'))}
            required
          />
          <AppSelectCountry
            name="useCase.address.country"
            control={control}
            label={capitalize(t('country', { count: 1 }))}
            required
          />

          <div className="flex gap-4">
            <AppInput
              control={control}
              name="useCase.address.state"
              label={t('state_slash_province')}
              placeholder={t('state_slash_province')}
              required
            />
            <AppInput
              control={control}
              name="useCase.address.zip"
              label={capitalize(t('postal_code'))}
              placeholder={capitalize(t('postal_code'))}
            />
          </div>
        </div>
      </AppModal.Body>
    </AppModal>
  );
}
